import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import {
  IonButton,
  IonPage,
  IonContent,
  IonHeader,
  IonLabel,
  IonList,
  IonIcon,
  IonGrid,
  IonCol,
  IonRefresher,
  IonRefresherContent,
  IonRow,
  IonSelect,
  IonSelectOption,
  IonPopover,
  IonSpinner,
} from '@ionic/react';
import { add, remove, funnel} from 'ionicons/icons';
import {
  getAllSelfEmployedJobs,
  getSelfEmployedJobsCount,
  getSelfEmployedJobsStats,
  getLastVisitedJob, getAllPropertiesNano
} from '../../utilities/Storage';
import {
  storeSelfEmployedJobs, storeSelfEmployedJobsCount, requestJobData,
  storeSelfEmployedPropertiesNano
} from '../../utilities/ApiTasks';
import { PaginatedFooter } from './PaginatedFooter';
import { PushNotificationRedirector } from '../../components/PushNotificationRedirector';
import { ClearFilter } from './ClearFilter';
import { PropertyFilter } from './PropertyFilter';
import { RequiredByDateFilter } from './RequiredByDateFilter';
import { JobRow } from './JobRow';
import { useHistory } from 'react-router-dom';
import { ApiContext } from '../../contexts/ApiContext';
import {Toolbar} from "../Toolbar";
import {AuthContext} from "../../contexts/AuthContext";

export default function JobsSelfEmployed() {
  const { dataInitialised, setSelfEmployedJobsCount, selfEmployedJobsCount } = useContext(ApiContext);
  const history = useHistory();

  const { user } = useContext(AuthContext);
  const [jobs, setJobs] = useState([]);
  const [jobsCountData, setJobsCountData] = useState(null);
  const [allProperties, setAllPropertiesNano] = useState(null);
  const [statusFilter, setStatusFilter] = useState('Approved');
  const [propertySearchTerm, setPropertySearchTerm] = useState('');
  const [dateOperator, setDateOperator] = useState('=');
  const [requiredByDate, setRequiredByDate] = useState('');
  const [currentPageNumber, setCurrentPageNumber] = useState(1);
  const [pageLimit] = useState(20);
  const [totalJobs, setTotalJobs] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingByPull, setIsLoadingByPull] = useState(false);
  const isPaginatingRef = useRef(false);
  const [popoverState, setShowPopover] = useState({
    showPopover: false,
    event: undefined,
  });

  const createQueryStringFilterValues = useCallback((selectedProperty=propertySearchTerm, selectedDate=requiredByDate, selectedOperator=dateOperator, selectedStatus=statusFilter) => ({
    status: selectedStatus,
    propertyId: selectedProperty,
    requiredBy: selectedDate
      ? `${selectedOperator}${selectedDate}`
      : '',
  }), [statusFilter, propertySearchTerm, dateOperator, requiredByDate]);

  const refresh = useCallback(async (
    pageNumber = currentPageNumber,
    refreshByPull = false,
    selectedProperty = propertySearchTerm,
    selectedDate = requiredByDate,
    selectedOperator = dateOperator,
    selectedStatus = statusFilter
  ) => {
    if (isPaginatingRef.current) return;
    isPaginatingRef.current = true;

    if(refreshByPull) {
      setIsLoadingByPull(true);
    } else {
      setIsLoading(true);
    }
    

    setCurrentPageNumber(pageNumber);
    setPropertySearchTerm(selectedProperty);
    setDateOperator(selectedOperator);
    setRequiredByDate(selectedDate);
    setStatusFilter(selectedStatus);

    await storeSelfEmployedJobs(
      pageNumber,
      pageLimit,
      createQueryStringFilterValues(selectedProperty, selectedDate, selectedOperator, selectedStatus),
    );

    await storeSelfEmployedJobsCount(
      createQueryStringFilterValues(selectedProperty, selectedDate, selectedOperator)
    );

    const jobs = await getAllSelfEmployedJobs();
    setJobs(jobs);

    const stats = await getSelfEmployedJobsStats();
    setTotalJobs(stats?.total?.value);
    setCurrentPageNumber(stats?.currentPage?.value);

    const selfEmployedJobsCount = await getSelfEmployedJobsCount();
    setJobsCountData(selfEmployedJobsCount);

    if(refreshByPull) {
      setIsLoadingByPull(false);
    } else {
      setIsLoading(false);
    }
    isPaginatingRef.current = false;
  }, [
    currentPageNumber,
    propertySearchTerm,
    requiredByDate,
    dateOperator,
    statusFilter,
    pageLimit,
    createQueryStringFilterValues,
  ]);

  useEffect(() => {
    let mounted = true;
    setIsLoading(true);

    if (!dataInitialised) return;

    const getData = async () => {

      let jobsStats = await getSelfEmployedJobsStats();
      if (!jobsStats?.currentPage?.length) {
          jobsStats = {
            currentPage: {
              value: 1
            },
            pageLimit: {
              value: 20
            },
            filters: {
              status: 'Approved'
            }
          }
      }
      if(!await storeSelfEmployedJobs(jobsStats.currentPage.value, jobsStats.pageLimit.value, jobsStats.filters)) { return; }

      const lastVisitedJob = await getLastVisitedJob();
      if(lastVisitedJob) {
        if(!await requestJobData(lastVisitedJob)) { return; }
      }

      const stats = await getSelfEmployedJobsStats();
      
      const allSelfEmployedJobs = await getAllSelfEmployedJobs();

      if (!mounted) return;

      setTotalJobs(stats?.total?.value);
      setCurrentPageNumber(stats?.currentPage?.value);
      setJobs(allSelfEmployedJobs);
      
      setIsLoading(false);

      if(!await storeSelfEmployedJobsCount(jobsStats.filters)) { return; }
      setSelfEmployedJobsCount(await getSelfEmployedJobsCount());
      const selfEmployedJobsCount = await getSelfEmployedJobsCount();
      setJobsCountData(selfEmployedJobsCount);
      if(!await storeSelfEmployedPropertiesNano(user.id)) { return; }
      const properties = await getAllPropertiesNano();
      if (allProperties === null) {
        setAllPropertiesNano(properties);
      }
    };

    if (dataInitialised) {
      getData();
    };

    return () => {
      mounted = false;
    };
  }, [dataInitialised]);


  useEffect(() => {
    let jobCountsUpdated = true;

    const getData = async () => {
      if(jobCountsUpdated) {
        const allSelfEmployedJobs = await getAllSelfEmployedJobs();
        const selfEmployedJobCounts = await getSelfEmployedJobsCount()
        setJobsCountData(selfEmployedJobCounts);
        setJobs(allSelfEmployedJobs);
      }
    }

    getData();
    
    return () => {
      jobCountsUpdated = false;
    }

   }, [selfEmployedJobsCount]);

  let numberOfFiltersApplied = 0;
  if (propertySearchTerm > 0) {
    numberOfFiltersApplied++;
  }
  if (requiredByDate?.length > 0) {
    numberOfFiltersApplied++;
  }

  const hasFiltersApplied = numberOfFiltersApplied > 0;
  const hasNoJobs = Object.values(jobs).length === 0;
  let rowIsGrey = false;

  return (
    <IonPage>
      <IonHeader>
        <Toolbar title='Jobs' backButtonHref='/'/>
      </IonHeader>

      <IonContent padding>
        <IonRefresher slot='fixed' pullMin={120} pullMax={180} onIonRefresh={event => {
          refresh(1, true).then(() => event.detail.complete());
        }}>
          <IonRefresherContent></IonRefresherContent>
        </IonRefresher>

        <IonGrid>
          <IonRow class='ion-align-items-center'>
            <IonCol size='10'>
              <IonSelect
                key={JSON.stringify(jobsCountData)}
                label='Job status'
                justify='start'
                fill='solid'
                interface='popover'
                toggleIcon={add}
                expandedIcon={remove}
                value={statusFilter}
                onIonChange={(e) => {
                  refresh(1, false, propertySearchTerm, requiredByDate, dateOperator, e.detail.value);
                }}
                aria-label='Status'
                class='full-width-select'
              >
                <IonSelectOption value='Approved'>
                  ({jobsCountData?.['Approved']?.value ?? 0}) Upcoming
                </IonSelectOption>

                <IonSelectOption value='Completed'>
                  ({jobsCountData?.['Completed']?.value ?? 0}) Costs to submit
                </IonSelectOption>

                <IonSelectOption value='Invoiced'>
                  ({jobsCountData?.['Invoiced']?.value ?? 0}) Pending cost approval
                </IonSelectOption>

                <IonSelectOption value='Invoice Rejected'>
                  ({jobsCountData?.['Invoice Rejected']?.value ?? 0}) Cost Rejected
                </IonSelectOption>

                <IonSelectOption value='Invoice Approved'>
                  ({jobsCountData?.['Invoice Approved']?.value ?? 0}) Accepted
                </IonSelectOption>
              </IonSelect>
            </IonCol>

            <IonCol size='2'>
              <IonPopover
                event={popoverState.event}
                isOpen={popoverState.showPopover}
                onDidDismiss={() => setShowPopover({ showPopover: false, event: undefined })}
              >
                <IonGrid fixed={true}>
                  <IonRow class='ion-align-items-center'>
                    <IonCol size='12'>
                      <PropertyFilter
                        properties={allProperties}
                        value={propertySearchTerm}
                        onChange={(propertySearchTerm) => {
                          refresh(1, false, propertySearchTerm, requiredByDate, dateOperator, statusFilter);
                        }}
                      />
                    </IonCol>
                  </IonRow>

                  <IonRow class='ion-align-items-center'>
                    <IonCol size='12'>
                      <RequiredByDateFilter
                        operator={dateOperator}
                        date={requiredByDate}
                        onOperatorChange={(dateOperator) => {
                          requiredByDate
                            ? refresh(1, false, propertySearchTerm, requiredByDate, dateOperator)
                            : setDateOperator(dateOperator);
                        }}
                        onRequiredByDateChange={(requiredByDate) => {
                          refresh(1, false, propertySearchTerm, requiredByDate, dateOperator, statusFilter);
                        }}
                      />
                    </IonCol>
                  </IonRow>

                  <IonRow>
                    <IonCol className='ion-text-right'>
                      <ClearFilter
                        onClick={() => {
                          refresh(1, false, '', '', '=', statusFilter);
                          setShowPopover({ showPopover: false, event: undefined });
                        }}/>
                    </IonCol>
                  </IonRow>
                </IonGrid>
              </IonPopover>

              <IonButton
                color='fern'
                size='medium'
                expand='block'
                onClick={(e) =>
                  setShowPopover({ showPopover: true, event: e.nativeEvent })
                }
              >
                <IonIcon icon={funnel} size='small' />
                {hasFiltersApplied && <IonLabel> <small>{numberOfFiltersApplied}</small></IonLabel>}
              </IonButton>
            </IonCol>
          </IonRow>
        </IonGrid>

        {isLoading ? (
          <div className='spinner-container'>
            <IonSpinner color='teal' />
          </div>
        ) : (

        <IonList lines='full'>
          {hasNoJobs ? (
                <h3 key='nojobs' className='ion-text-center'>No jobs found.</h3>
              ) : (
                jobs.map((job) =>{
                    rowIsGrey = !rowIsGrey;
                    const color = rowIsGrey ? 'lgrey' : 'white';

                return (
                    <JobRow
                        key={job.value.id}
                        color={color}
                        job={job.value}
                        history={history}
                    />
                );
              })
          )
          }
        </IonList>
      )}
      </IonContent>
      <PaginatedFooter
        currentPageNumber={currentPageNumber}
        itemsCount={totalJobs}
        pageLimit={pageLimit}
        onPageChange={(page) => {
          refresh(page, false);
        }}
        isLoading={isLoading}
      />

      <PushNotificationRedirector />
    </IonPage>
  );
}
